import React, { useMemo, useState } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { useAccess } from "features/Authentication/hooks/useAccess";
import type { PettyWork } from "features/PettyWorks/types/petty-work.types";
import {
  getPettyWorkLifecycleVisibility,
  pettyWorkLifecycleAnyVisible,
} from "features/PettyWorks/utils/petty-work-lifecycle.utils";
import PettyWorkLifecycleModals, {
  type PettyWorkLifecycleIntent,
} from "./PettyWorkLifecycleModals";

type Props = {
  pettyWork: PettyWork;
  onSuccess: () => void | Promise<void>;
};

export default function PettyWorkLifecycleButtons({
  pettyWork,
  onSuccess,
}: Props) {
  const { can } = useAccess();
  const [intent, setIntent] = useState<PettyWorkLifecycleIntent | null>(null);

  const vis = useMemo(
    () => getPettyWorkLifecycleVisibility(pettyWork, can),
    [pettyWork, can],
  );

  if (!pettyWorkLifecycleAnyVisible(vis)) {
    return null;
  }

  const btnClass =
    "btn btn-sm d-inline-flex align-items-center gap-1 flex-shrink-0";

  const hasDropdown = vis.cancel || vis.rejectCompletion;

  return (
    <>
      <div className="hstack gap-2 flex-wrap align-items-center">
        {vis.start ? (
          <button
            type="button"
            className={`${btnClass} btn-primary`}
            onClick={() => setIntent({ kind: "start" })}
          >
            <i className="ri-play-circle-line align-bottom" />
            Start work
          </button>
        ) : null}
        {vis.hold ? (
          <button
            type="button"
            className={`${btnClass} btn-warning`}
            onClick={() => setIntent({ kind: "hold" })}
          >
            <i className="ri-pause-circle-line align-bottom" />
            Hold
          </button>
        ) : null}
        {vis.resume ? (
          <button
            type="button"
            className={`${btnClass} btn-success`}
            onClick={() => setIntent({ kind: "resume" })}
          >
            <i className="ri-play-circle-line align-bottom" />
            Resume
          </button>
        ) : null}
        {vis.submitCompletion ? (
          <button
            type="button"
            className={`${btnClass} btn-success`}
            onClick={() => setIntent({ kind: "submitCompletion" })}
          >
            <i className="ri-checkbox-circle-line align-bottom" />
            Submit completion
          </button>
        ) : null}
        {vis.approveForBilling ? (
          <button
            type="button"
            className={`${btnClass} btn-success`}
            onClick={() => setIntent({ kind: "approveForBilling" })}
          >
            <i className="ri-check-double-line align-bottom" />
            Approve for billing
          </button>
        ) : null}
        {vis.submitBilling ? (
          <button
            type="button"
            className={`${btnClass} btn-primary`}
            onClick={() => setIntent({ kind: "submitBilling" })}
          >
            <i className="ri-bill-line align-bottom" />
            Submit billing
          </button>
        ) : null}
        {hasDropdown ? (
          <UncontrolledDropdown>
            <DropdownToggle
              type="button"
              className="btn btn-light btn-sm border"
            >
              <i className="ri-more-2-fill align-middle" />
            </DropdownToggle>
            <DropdownMenu end>
              {vis.rejectCompletion ? (
                <DropdownItem
                  className="text-danger"
                  onClick={() => setIntent({ kind: "rejectCompletion" })}
                >
                  <i className="ri-close-circle-line align-bottom me-2" />
                  Reject completion
                </DropdownItem>
              ) : null}
              {vis.cancel ? (
                <DropdownItem
                  className="text-danger"
                  onClick={() => setIntent({ kind: "cancel" })}
                >
                  <i className="ri-close-circle-line align-bottom me-2" />
                  Cancel petty work
                </DropdownItem>
              ) : null}
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : null}
      </div>

      <PettyWorkLifecycleModals
        pettyWork={pettyWork}
        intent={intent}
        onFinished={() => setIntent(null)}
        onSuccess={onSuccess}
      />
    </>
  );
}
